import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  CreditCardIcon,
  BanknotesIcon,
  CheckIcon,
  ClockIcon,
  ReceiptRefundIcon,
  ShoppingCartIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import AttributeLabel from '../components/AttributeLabel';
import { StatCard, StatGroup } from '../components/StatCard';
import { ModalShell, ModalActions, ModalSection, ModalRow } from '../components/modals/core';

interface Drink {
  id: number;
  month: number;
  event_label: string;
  amount: number;
  paid: number;
  paid_date: string | null;
  payment_method: string | null;
  token: string;
  abrechnungsmonat?: string | null;
}

const MyDrinks: React.FC = () => {
  const [drinks, setDrinks] = useState<Drink[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDrink, setSelectedDrink] = useState<Drink | null>(null);
  const [selectedDrinks, setSelectedDrinks] = useState<Drink[]>([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState<'unpaid' | 'paid'>('unpaid');
  const [showPayAllModal, setShowPayAllModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(() => new Date().getFullYear());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const drinksRes = await apiClient.get('/api/drinks/mydrinks');
      const drinksData = drinksRes.data || [];

      const sanitizedDrinks = drinksData.map((drink: any) => ({
        ...drink,
        amount: isNaN(parseFloat(drink.amount)) ? 0 : parseFloat(drink.amount),
      }));

      setDrinks(sanitizedDrinks);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setMessage({
          type: 'error',
          text: err.message || 'Fehler beim Laden der Daten.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalClick = (drink: Drink) => {
    setSelectedDrink(drink);
    setModalOpen(true);
  };

  const handlePayAllClick = () => {
    setSelectedDrinks(openDrinks);
    setShowPayAllModal(true);
  };

  const calculatePayPalFees = (amount: number) => {
    return amount * 1.0249 + 0.35;
  };

  const handlePayAllConfirm = (useSandbox: boolean = false) => {
    if (selectedDrinks.length > 0) {
      const totalAmount = selectedDrinks.reduce((sum, drink) => sum + drink.amount, 0);
      const totalWithFees = calculatePayPalFees(totalAmount).toFixed(2);
      const customTokens = selectedDrinks.map(d => d.token).join(';');

      const returnUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-success');
      const cancelUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-cancel');

      const baseUrl = useSandbox ? 'https://www.sandbox.paypal.com' : 'https://www.paypal.com';
      const businessEmail = useSandbox ? 'sb-qvwew31972863@business.example.com' : 'markus@tietke.com';
      const paypalLink = `${baseUrl}/cgi-bin/webscr?cmd=_xclick&business=${businessEmail}&item_name=HSV-Adlershof-Verzehrkosten&amount=${totalWithFees}&currency_code=EUR&no_shipping=1&bn=PP-FamilyFriends&custom=${customTokens}&return=${returnUrl}&cancel_return=${cancelUrl}`;
      window.open(paypalLink, '_blank');

      setMessage({
        type: 'info',
        text: useSandbox
          ? 'PayPal-Sandbox-Zahlung wurde in einem neuen Fenster geöffnet (Testmodus).'
          : 'PayPal-Zahlung wurde in einem neuen Fenster geöffnet. Bitte schließen Sie die Zahlung dort ab.'
      });

      setTimeout(() => {
        fetchData();
      }, 5000);
    }
    setShowPayAllModal(false);
    setSelectedDrinks([]);
  };

  const handlePayPalConfirm = (useSandbox: boolean = false) => {
    if (selectedDrink) {
      const totalWithFees = calculatePayPalFees(selectedDrink.amount).toFixed(2);

      const returnUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-success');
      const cancelUrl = encodeURIComponent('https://intern.hsv-adlershof.berlin/paypal-cancel');

      const baseUrl = useSandbox ? 'https://www.sandbox.paypal.com' : 'https://www.paypal.com';
      const businessEmail = useSandbox ? 'sb-qvwew31972863@business.example.com' : 'markus@tietke.com';
      const paypalLink = `${baseUrl}/cgi-bin/webscr?cmd=_xclick&business=${businessEmail}&item_name=HSV-Adlershof-${selectedDrink.event_label}&amount=${totalWithFees}&currency_code=EUR&no_shipping=1&bn=PP-FamilyFriends&custom=${selectedDrink.token}&return=${returnUrl}&cancel_return=${cancelUrl}`;
      window.open(paypalLink, '_blank');

      setMessage({
        type: 'info',
        text: useSandbox
          ? 'PayPal-Sandbox-Zahlung wurde in einem neuen Fenster geöffnet (Testmodus).'
          : 'PayPal-Zahlung wurde in einem neuen Fenster geöffnet. Bitte schließen Sie die Zahlung dort ab.'
      });

      setTimeout(() => {
        fetchData();
      }, 5000);
    }
    setModalOpen(false);
    setSelectedDrink(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDrink(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return '-';
    }
  };

  const formatEventMonth = (dateStr?: string | null) => {
    if (!dateStr) return 'Abrechnungsmonat fehlt';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return 'Abrechnungsmonat fehlt';
    return new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(date);
  };

  const getPaymentMethodLabel = (method: string | null) => {
    switch(method) {
      case 'cash':
      case 'bar':
        return 'Bar';
      case 'paypal':
        return 'PayPal';
      case 'bank_transfer':
        return 'Überweisung';
      default:
        return method || 'Unbekannt';
    }
  };

  const availableYears = useMemo(() => Array.from(new Set(
    drinks
      .map(drink => drink.abrechnungsmonat ? new Date(drink.abrechnungsmonat).getFullYear() : null)
      .filter((year): year is number => year !== null && !Number.isNaN(year))
  )).sort((a, b) => b - a), [drinks]);

  const filteredDrinks = useMemo(() => drinks.filter(drink => selectedYear === 'all' || (
    drink.abrechnungsmonat && new Date(drink.abrechnungsmonat).getFullYear() === selectedYear
  )), [drinks, selectedYear]);

  const sortByEventDate = (a: Drink, b: Drink) => {
    const aTime = a.abrechnungsmonat ? new Date(a.abrechnungsmonat).getTime() : 0;
    const bTime = b.abrechnungsmonat ? new Date(b.abrechnungsmonat).getTime() : 0;
    return bTime - aTime || Number(b.month) - Number(a.month);
  };

  const openDrinks = filteredDrinks
    .filter((d) => d.paid === 0)
    .sort(sortByEventDate);

  const paidDrinks = filteredDrinks
    .filter((d) => d.paid === 1)
    .sort(sortByEventDate);

  const totalOpen = openDrinks.reduce((sum, d) => sum + d.amount, 0);
  const totalPaid = paidDrinks.reduce((sum, d) => sum + d.amount, 0);
  const activeDrinks = activeTab === 'unpaid' ? openDrinks : paidDrinks;
  const periodTotal = totalOpen + totalPaid;

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
      <section className="app-page-header overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-5 p-5 sm:p-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <ShoppingCartIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Persönliche Abrechnung</p>
              <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">Meine Verzehrkosten</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Alle Beiträge nach Abrechnungsmonat und Zahlungsstatus</p>
            </div>
          </div>
          <div className="flex w-full items-center gap-2 lg:w-auto">
            <AttributeLabel iconKey="year" htmlFor="my-drinks-year" className="sr-only">Abrechnungsjahr</AttributeLabel>
            <select id="my-drinks-year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value === 'all' ? 'all' : Number(e.target.value))} className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-bold text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white lg:w-auto">
              <option value="all">Alle Jahre</option>
              {availableYears.map(year => <option key={year} value={year}>Jahr {year}</option>)}
            </select>
          </div>
        </div>
      </section>

      {message.text && (
        <div className={`flex items-center gap-3 rounded-xl border p-4 ${message.type === 'success' ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400' : message.type === 'error' ? 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-400' : 'border-info-200 bg-info-50 text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300'}`}>
          {message.type === 'success' ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationCircleIcon className="h-5 w-5" />}
          <span className="flex-1 text-sm font-semibold">{message.text}</span>
          <button onClick={() => setMessage({ type: '', text: '' })} className="rounded-lg p-1 hover:bg-black/5"><XMarkIcon className="h-5 w-5" /></button>
        </div>
      )}

      <StatGroup columns={3}>
        <StatCard
          icon={ClockIcon}
          tone="error"
          valueTone="error"
          label="Offen"
          value={formatCurrency(totalOpen)}
          sub={`${openDrinks.length} ${openDrinks.length === 1 ? 'Beitrag' : 'Beiträge'}`}
        />
        <StatCard
          icon={CheckCircleIcon}
          tone="success"
          valueTone="success"
          label="Bezahlt"
          value={formatCurrency(totalPaid)}
          sub={`${paidDrinks.length} ${paidDrinks.length === 1 ? 'Beitrag' : 'Beiträge'}`}
        />
        <StatCard
          icon={BanknotesIcon}
          label="Gesamt"
          value={formatCurrency(periodTotal)}
          sub={selectedYear === 'all' ? 'Alle Jahre' : `Jahr ${selectedYear}`}
        />
      </StatGroup>

      {totalOpen > 0 && (
        <section className="rounded-2xl border border-warning-200 bg-warning-50 p-5 dark:border-warning-800 dark:bg-warning-900/20 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-warning-700 dark:text-warning-400">Zahlung ausstehend</p>
              <h2 className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{openDrinks.length === 1 ? 'Ein Beitrag ist noch offen' : `${openDrinks.length} Beiträge sind noch offen`}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Du kannst {openDrinks.length === 1 ? 'den Beitrag' : 'alle sichtbaren Beiträge'} gemeinsam per PayPal bezahlen.</p>
            </div>
            <button onClick={openDrinks.length === 1 ? () => handlePayPalClick(openDrinks[0]) : handlePayAllClick} className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 text-sm font-bold text-white transition hover:bg-primary-700">
              <CreditCardIcon className="h-5 w-5" /> {openDrinks.length === 1 ? 'Jetzt bezahlen' : 'Alle bezahlen'}
            </button>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex rounded-xl bg-gray-100 p-1 dark:bg-gray-700">
            <button onClick={() => setActiveTab('unpaid')} className={`flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold transition ${activeTab === 'unpaid' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-300'}`}><ClockIcon className="h-4 w-4" /> Offen <span className="rounded-full bg-error-100 px-2 py-0.5 text-[11px] text-error-700 dark:bg-error-900/30 dark:text-error-400">{openDrinks.length}</span></button>
            <button onClick={() => setActiveTab('paid')} className={`flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-bold transition ${activeTab === 'paid' ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-300'}`}><CheckCircleIcon className="h-4 w-4" /> Bezahlt <span className="rounded-full bg-success-100 px-2 py-0.5 text-[11px] text-success-700 dark:bg-success-900/30 dark:text-success-400">{paidDrinks.length}</span></button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">{selectedYear === 'all' ? 'Alle Jahre' : selectedYear} · nach Abrechnungsmonat sortiert</p>
        </div>

        {activeDrinks.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {activeDrinks.map(drink => (
              <div key={drink.id} className="flex items-center gap-3 p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60 sm:gap-4 sm:px-5">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${drink.paid ? 'bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400' : 'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400'}`}>
                  {drink.paid ? <CheckIcon className="h-5 w-5" /> : <ShoppingCartIcon className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-bold text-gray-900 dark:text-white sm:text-base">{drink.event_label}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1 font-semibold text-primary-600 dark:text-primary-400"><CalendarIcon className="h-3.5 w-3.5" /> {formatEventMonth(drink.abrechnungsmonat)}</span>
                    {drink.paid ? <><span>•</span><span>bezahlt am {formatDate(drink.paid_date)}</span><span>•</span><span>{getPaymentMethodLabel(drink.payment_method)}</span></> : <><span>•</span><span>Zahlung offen</span></>}
                  </div>
                </div>
                <div className={`shrink-0 text-base font-bold tabular-nums ${drink.paid ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>{formatCurrency(drink.amount)}</div>
                {!drink.paid && <button onClick={() => handlePayPalClick(drink)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white transition hover:bg-primary-700" aria-label={`${drink.event_label} bezahlen`}><ChevronRightIcon className="h-5 w-5" /></button>}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-14 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">{activeTab === 'unpaid' ? <CheckCircleIcon className="h-8 w-8 text-success-500" /> : <ReceiptRefundIcon className="h-8 w-8 text-gray-400" />}</div>
            <h3 className="mt-4 font-bold text-gray-900 dark:text-white">{filteredDrinks.length === 0 ? 'Keine Beiträge in diesem Zeitraum' : activeTab === 'unpaid' ? 'Alles bezahlt' : 'Noch keine bezahlten Beiträge'}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{filteredDrinks.length === 0 ? 'Wähle ein anderes Abrechnungsjahr aus.' : activeTab === 'unpaid' ? 'Für den gewählten Zeitraum bestehen keine offenen Verzehrkosten.' : 'Bezahlte Beiträge erscheinen später hier.'}</p>
          </div>
        )}
      </section>

      {/* PayPal Confirmation Modal - Design Guide Section 15 */}
      {selectedDrink && (
        <ModalShell
          open={modalOpen}
          onClose={handleModalClose}
          variant="confirm"
          size="sm"
          title="Zahlung bestätigen"
          subtitle={`${selectedDrink.event_label} · ${formatEventMonth(selectedDrink.abrechnungsmonat)}`}
          icon={CreditCardIcon}
          mobilePresentation="sheet"
          footer={
            <ModalActions
              secondary={{ label: 'Abbrechen', onClick: handleModalClose }}
              primary={{
                label: 'Mit PayPal bezahlen',
                onClick: () => handlePayPalConfirm(false),
                icon: <CreditCardIcon className="h-5 w-5" />,
              }}
            />
          }
        >
          <ModalSection>
            <div className="rounded-2xl p-6 text-center bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800">
              <AttributeLabel iconKey="amount" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Gesamtbetrag
              </AttributeLabel>
              <div className="text-4xl font-bold text-gray-900 dark:text-white my-2">
                {formatCurrency(selectedDrink.amount * 1.0249 + 0.35)}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                inkl. PayPal-Gebühren
              </div>
            </div>
          </ModalSection>

          <ModalSection className="mt-5">
            <ModalRow label={<AttributeLabel iconKey="amount">Beitrag</AttributeLabel>}>
              {formatCurrency(selectedDrink.amount)}
            </ModalRow>
            <ModalRow label="PayPal-Gebühren">
              {formatCurrency(selectedDrink.amount * 0.0249 + 0.35)}
            </ModalRow>
          </ModalSection>

          <ModalSection className="mt-5 space-y-4">
            <div className="rounded-2xl p-4 flex gap-3 bg-info-100 dark:bg-info-900/30 border border-info-200 dark:border-info-800">
              <ExclamationCircleIcon className="h-5 w-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-info-800 dark:text-info-200">
                <p className="mb-2">
                  Die Zahlung wird in einem neuen Tab über PayPal abgewickelt.
                </p>
                <p>
                  Alternativ können Sie den Beitrag jederzeit bar beim Kassenwart bezahlen.
                </p>
              </div>
            </div>

            {/* Fee Saving Hint */}
            {openDrinks.length > 1 && (
              <div className="rounded-2xl p-4 flex gap-3 bg-success-100 dark:bg-success-900/30 border border-success-200 dark:border-success-800">
                <BanknotesIcon className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-success-800 dark:text-success-200">
                  <p className="font-semibold mb-2">
                    Tipp: Gebühren sparen!
                  </p>
                  <p>
                    Mit "Alle bezahlen" können Sie alle {openDrinks.length} Positionen zusammenfassen und{' '}
                    <strong>{formatCurrency((openDrinks.length * 0.35) - 0.35)}</strong> sparen!
                  </p>
                </div>
              </div>
            )}
          </ModalSection>
        </ModalShell>
      )}

      {/* Pay All Confirmation Modal */}
      {selectedDrinks.length > 0 && (
        <ModalShell
          open={showPayAllModal}
          onClose={() => {
            setShowPayAllModal(false);
            setSelectedDrinks([]);
          }}
          variant="confirm"
          size="md"
          title="Sammelzahlung bestätigen"
          subtitle={`${selectedDrinks.length} Positionen zusammengefasst`}
          icon={CreditCardIcon}
          mobilePresentation="sheet"
          footer={
            <ModalActions
              secondary={{
                label: 'Abbrechen',
                onClick: () => {
                  setShowPayAllModal(false);
                  setSelectedDrinks([]);
                },
              }}
              primary={{
                label: 'Alle mit PayPal bezahlen',
                onClick: () => handlePayAllConfirm(false),
                icon: <CreditCardIcon className="h-5 w-5" />,
              }}
            />
          }
        >
          <ModalSection>
            <div className="rounded-2xl p-6 text-center bg-primary-100 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800">
              <AttributeLabel iconKey="amount" textClassName="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                Gesamtbetrag
              </AttributeLabel>
              <div className="text-4xl font-bold text-gray-900 dark:text-white my-2">
                {formatCurrency(calculatePayPalFees(selectedDrinks.reduce((sum, d) => sum + d.amount, 0)))}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                inkl. PayPal-Gebühren
              </div>
            </div>
          </ModalSection>

          <ModalSection title="Einzelpositionen" className="mt-5">
            <div className="rounded-2xl p-4 bg-gray-100 dark:bg-gray-700">
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedDrinks.map((drink, index) => (
                  <div key={drink.id} className="flex items-start justify-between gap-3 text-sm">
                    <span className="min-w-0 text-gray-500 dark:text-gray-400">
                      <span className="block truncate">{index + 1}. {drink.event_label}</span>
                      <span className="mt-0.5 block text-xs text-primary-600 dark:text-primary-400">{formatEventMonth(drink.abrechnungsmonat)}</span>
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(drink.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ModalSection>

          <ModalSection className="mt-5">
            <ModalRow label={<AttributeLabel iconKey="amount">Summe Verzehrkosten</AttributeLabel>}>
              {formatCurrency(selectedDrinks.reduce((sum, d) => sum + d.amount, 0))}
            </ModalRow>
            <ModalRow label="PayPal-Gebühren (2,49% + 0,35€)">
              {formatCurrency(
                calculatePayPalFees(selectedDrinks.reduce((sum, d) => sum + d.amount, 0)) -
                selectedDrinks.reduce((sum, d) => sum + d.amount, 0)
              )}
            </ModalRow>
          </ModalSection>

          <ModalSection className="mt-5">
            <div className="rounded-2xl p-4 flex gap-3 bg-info-100 dark:bg-info-900/30 border border-info-200 dark:border-info-800">
              <ExclamationCircleIcon className="h-5 w-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-info-800 dark:text-info-200">
                <p className="mb-2">
                  <strong>Wichtig:</strong> Alle {selectedDrinks.length} Positionen werden in einer Zahlung zusammengefasst.
                </p>
                <p>
                  Die Zahlung wird in einem neuen Tab über PayPal abgewickelt.
                </p>
              </div>
            </div>
          </ModalSection>
        </ModalShell>
      )}
    </div>
  );
};

export default MyDrinks;
